/**
 * LAN-direct storage acceleration helpers.
 *
 * When a user's browser is on the office LAN (192.168.0.x) or branch VPN
 * (10.0.1.x), we can bypass the Cloudflare tunnel and fetch NAS files
 * directly. This module:
 *
 *   1. probeLan()        — races the admin-configured probe URLs; the first
 *                          one that responds < 500ms wins and is cached in
 *                          sessionStorage for the whole tab session.
 *   2. getLanBase()      — read cached winner ("" if no LAN detected).
 *   3. clearLanBase()    — force-reset (used when a signed fetch fails).
 *   4. rewriteToLan(url) — replace host component of a signed WAN URL with
 *                          the LAN base. HMAC covers method+path+exp only
 *                          (host-agnostic on purpose), so this is safe.
 *
 * Failure model (per user choice #5): if the LAN base is set but a specific
 * download times out, we silently fall back to WAN and RETRY LAN on the
 * next download. We don't invalidate the LAN base for one bad response —
 * transient network hiccups are common on Wi-Fi.
 */

const CACHE_KEY = 'sensbey_lan_base_v1';
// 2500ms — comfortable for a fresh TLS handshake + first-hit response.
// First-time TLS negotiation to a new host can easily eat 300-800ms on
// Wi-Fi. 500ms (the initial value) turned out to be too aggressive and
// caused false-negative "no LAN found" for real LAN clients.
const PROBE_TIMEOUT_MS = 2500;

/** Read the cached LAN base URL for this session ("" if not detected). */
export function getLanBase() {
  try {
    return sessionStorage.getItem(CACHE_KEY) || '';
  } catch {
    return '';
  }
}

function setLanBase(url) {
  try {
    if (url) sessionStorage.setItem(CACHE_KEY, url);
    else sessionStorage.removeItem(CACHE_KEY);
  } catch { /* private mode / disabled — LAN just stays off */ }
  // Broadcast so the header badge re-renders
  try { window.dispatchEvent(new CustomEvent('lan-base-changed', { detail: url })); } catch (_e) { /* headless / no window */ }
}

/** Force-reset (rare — only if the whole LAN base becomes unreachable). */
export function clearLanBase() {
  setLanBase('');
}

/** Probe one URL with a hard timeout. Resolves to the URL on success, null on failure. */
async function probeOne(base) {
  const url = base.replace(/\/$/, '') + '/lan-check';
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: ctrl.signal,
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const j = await res.json().catch(() => ({}));
    return j && j.ok ? base.replace(/\/$/, '') : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Race-probe the given base URLs; store the first one that answers < 500ms.
 * Returns the winning base URL (or "" if none reachable).
 *
 * Idempotent — safe to call on every login. Only writes sessionStorage if
 * the winner is different from the current cache to avoid pointless event
 * broadcasts.
 */
export async function probeLan(baseUrls = []) {
  const candidates = (baseUrls || []).filter(Boolean);
  if (!candidates.length) {
    if (getLanBase()) setLanBase('');
    return '';
  }
  // Fire all probes in parallel; take the first non-null result.
  const results = await Promise.all(candidates.map(probeOne));
  const winner = results.find((r) => !!r) || '';
  if (winner !== getLanBase()) setLanBase(winner);
  return winner;
}

/**
 * Rewrite the host of a signed WAN URL to point at the LAN base.
 * Preserves protocol/port from the LAN base and path+query from the input.
 *
 * If the LAN base is empty OR the input URL is malformed, returns the
 * original URL untouched (caller decides whether to still fetch).
 */
export function rewriteToLan(signedUrl) {
  const base = getLanBase();
  if (!base || !signedUrl) return signedUrl;
  try {
    const src = new URL(signedUrl);
    const lan = new URL(base);
    src.protocol = lan.protocol;
    src.host = lan.host; // includes port
    return src.toString();
  } catch {
    return signedUrl;
  }
}

/**
 * Two-phase LAN-direct upload.
 *
 * When the browser has a cached LAN base AND the app's storage backend is
 * NAS, this uploads the file bytes DIRECTLY to the NAS over the local
 * network — bypassing the Cloudflare tunnel entirely. Otherwise, it falls
 * back to the caller-provided `legacyUpload(file, extra)` function.
 *
 * Params
 *   axios       — the shared axios instance (imported by caller, avoids
 *                  a hard dependency here)
 *   apiBase     — `${REACT_APP_BACKEND_URL}/api`
 *   kind        — one of the backend UPLOAD_KINDS ("shizai_receipt" etc)
 *   file        — the File / Blob to upload
 *   extra       — kind-specific metadata (e.g. entity_type/entity_id for mfg)
 *   legacyUpload— async function that runs the ORIGINAL upload endpoint
 *                  (POST multipart/form-data). Returns the same shape the
 *                  complete-endpoint response would have — this preserves
 *                  the calling component's error/happy path unchanged.
 *   onProgress  — optional (loaded, total) → void progress callback
 *
 * Returns whatever `legacyUpload` returns (on fallback) OR the complete-
 * endpoint response (on LAN success). Callers should treat them identically.
 */
export async function lanUpload({ axios, apiBase, kind, file, extra = {}, legacyUpload, onProgress }) {
  const runFallback = () => legacyUpload(file, extra);
  // Only try LAN when a base is currently cached
  if (!getLanBase()) return runFallback();
  // 1. INIT — allocates a signed PUT URL if the app is on NAS
  let init;
  try {
    const initRes = await axios.post(`${apiBase}/storage/lan-upload/init`, {
      kind,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
      extra,
    }, { withCredentials: true, timeout: 5000 });
    init = initRes.data || {};
  } catch {
    return runFallback();
  }
  if (!init.direct_url || !init.upload_id || !init.storage_path) {
    return runFallback();
  }
  // 2. PUT — rewrite host to LAN, upload bytes directly to NAS
  const lanUrl = rewriteToLan(init.direct_url);
  try {
    await axios.put(lanUrl, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      timeout: 60000,
      withCredentials: false,
      onUploadProgress: onProgress
        ? (evt) => { if (evt.total) onProgress(evt.loaded, evt.total); }
        : undefined,
    });
  } catch (_e) {
    // NAS unreachable OR CORS blocked (mixed-content on HTTPS pages
    // is normal). Silent WAN fallback.
    return runFallback();
  }
  // 3. COMPLETE — server HEADs NAS + inserts DB row
  try {
    const completeRes = await axios.post(`${apiBase}/storage/lan-upload/complete`, {
      upload_id: init.upload_id,
      storage_path: init.storage_path,
      expires_at: init.expires_at,
      kind,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
      extra,
    }, { withCredentials: true, timeout: 15000 });
    return completeRes.data;
  } catch {
    // File is orphaned on NAS but we didn't insert a DB row — that's fine,
    // the file is unreferenced and can be cleaned up later. Still, best
    // to fall back so the user gets a working outcome.
    return runFallback();
  }
}
